/**
 * WP-231B pure push helpers used by the service worker (kept DOM/SW-free so they unit-test).
 * The server sends `{ kind, title, body, url, conversationId?, tag?, badgeCount? }` already
 * localized; the body is at most the contact display name (never message text).
 */
export const PUSH_ICON_URL = "/icons/icon-192.png";
export const PUSH_BADGE_URL = "/icons/icon-192.png";

export interface PushNotificationSpec {
  readonly title: string;
  readonly options: {
    readonly body: string;
    readonly tag: string;
    /** Always true: a tag is always set, and a replacing notification must still alert. */
    readonly renotify: true;
    readonly icon: string;
    readonly badge: string;
    readonly dir: "rtl" | "ltr";
    readonly lang: string;
    readonly data: { readonly url: string };
  };
}

/** Only the path is returned, so any well-formed origin works when the caller has none. */
const FALLBACK_ORIGIN = "https://app.invalid";
// Backslash, C0/C1 controls, whitespace (incl. tab/newline, NBSP-class and line separators).
const UNSAFE_URL_CHARS =
  // eslint-disable-next-line no-control-regex
  /[\\\u0000-\u0020\u007f-\u00a0\u1680\u2000-\u200f\u2028-\u202f\u205f-\u2064\u3000\ufeff]/;

/**
 * Same-origin path only, returned as path + search + hash. Anything else (absolute or
 * protocol-relative URLs, `javascript:`, backslash/control/whitespace tricks such as `/\evil.com`)
 * falls back to the inbox root `/`.
 */
export function safeNotificationUrl(url: unknown, origin: string = FALLBACK_ORIGIN): string {
  if (typeof url !== "string" || !url.startsWith("/") || url.startsWith("//")) return "/";
  if (UNSAFE_URL_CHARS.test(url)) return "/";
  try {
    const parsed = new URL(url, origin);
    if (parsed.origin !== new URL(origin).origin) return "/";
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return "/";
  }
}

export function pushPayloadToNotification(
  data: unknown,
  origin?: string,
): PushNotificationSpec | null {
  if (typeof data !== "object" || data === null) return null;
  const d = data as Record<string, unknown>;
  if (typeof d.title !== "string" || d.title === "") return null;
  const conversationId = typeof d.conversationId === "string" ? d.conversationId : null;
  const kind = typeof d.kind === "string" ? d.kind : "push";
  const explicitTag = typeof d.tag === "string" && d.tag !== "" ? d.tag : null;
  const lang = d.lang === "en" ? "en" : "ar";
  return {
    title: d.title,
    options: {
      body: typeof d.body === "string" ? d.body : "",
      // One notification per conversation: a repeat replaces instead of stacking.
      tag: explicitTag ?? (conversationId ? `conversation:${conversationId}` : kind),
      renotify: true,
      icon: PUSH_ICON_URL,
      badge: PUSH_BADGE_URL,
      dir: lang === "ar" ? "rtl" : "ltr",
      lang,
      data: { url: safeNotificationUrl(d.url, origin) },
    },
  };
}

/** The unread total to put on the app icon: a non-negative integer, else `null` (leave it as is). */
export function pushBadgeCount(data: unknown): number | null {
  if (typeof data !== "object" || data === null) return null;
  const n = (data as Record<string, unknown>).badgeCount;
  return typeof n === "number" && Number.isInteger(n) && n >= 0 ? n : null;
}

/** The Badging API surface (`navigator.setAppBadge`), absent in most desktop browsers and Firefox. */
export interface BadgeNavigator {
  setAppBadge?(count?: number): Promise<void>;
  clearAppBadge?(): Promise<void>;
}

/** Best-effort app-icon badge: 0 clears it; unsupported or failing calls are ignored. */
export async function applyAppBadge(nav: BadgeNavigator | undefined, count: number): Promise<void> {
  if (!nav) return;
  try {
    if (count === 0) {
      if (nav.clearAppBadge) await nav.clearAppBadge();
      else if (nav.setAppBadge) await nav.setAppBadge(0);
      return;
    }
    if (nav.setAppBadge) await nav.setAppBadge(count);
  } catch {
    /* the badge is cosmetic: never let it break notification delivery */
  }
}

/** Clears the app-icon badge (the user is in the app and sees the unread counts there). */
export function clearAppBadge(nav: BadgeNavigator | undefined): Promise<void> {
  return applyAppBadge(nav, 0);
}

/** Focus an existing dashboard window (navigating it) when one is open, else open a new one. */
export function pickClientToFocus<T extends { readonly url: string }>(
  clients: readonly T[],
  origin: string,
): T | null {
  return clients.find((c) => c.url.startsWith(origin)) ?? null;
}

/** True when a dashboard window is visible/focused: push is then suppressed (FEAT-03). */
export function hasActiveClient(
  clients: readonly { url: string; focused?: boolean; visibilityState?: string }[],
  origin: string,
): boolean {
  return clients.some(
    (c) => c.url.startsWith(origin) && (c.focused === true || c.visibilityState === "visible"),
  );
}
